import React, { useMemo, useState } from 'react';
import {
  Alert,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { api } from '../../services/api';

const STATUS_OPTIONS = [
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'INACTIVE', value: 'INACTIVE' },
  { label: 'SUSPENDED', value: 'SUSPENDED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
];

const PLAN_OPTIONS = [
  { label: 'BASIC', value: 'BASIC' },
  { label: 'PRO', value: 'PRO' },
  { label: 'ENTERPRISE', value: 'ENTERPRISE' },
];

const SUBSCRIPTION_STATUS_OPTIONS = [
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'TRIAL', value: 'TRIAL' },
  { label: 'SUSPENDED', value: 'SUSPENDED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
];

const CreateTenantModal = ({ open, onClose, onCreated }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const initialValues = useMemo(
    () => ({
      status: 'ACTIVE',
      display_name: '',
      slug: '',
      legal_name: '',
      plan_code: undefined,
      subscription_status: undefined,
      trial_ends_at: null,
      public_signup_enabled: false,
    }),
    [],
  );

  const handleCancel = () => {
    if (saving) {
      return;
    }
    setError('');
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    setError('');

    try {
      const values = await form.validateFields();
      const payload = {
        display_name: String(values.display_name || '').trim(),
        slug: String(values.slug || '').trim(),
        legal_name: values.legal_name ? String(values.legal_name).trim() : undefined,
        status: values.status || 'ACTIVE',
      };

      if (values.plan_code) {
        payload.plan_code = values.plan_code;
      }

      if (values.subscription_status) {
        payload.subscription_status = values.subscription_status;
      }

      if (values.trial_ends_at) {
        const trialDate = dayjs(values.trial_ends_at);
        payload.trial_ends_at = trialDate.isValid() ? trialDate.toISOString() : undefined;
      }

      if (values.public_signup_enabled === true) {
        payload.public_signup_enabled = true;
      }

      setSaving(true);
      await api.post('/api/platform-admin/tenants', payload);
      message.success('Tenant criado com sucesso.');
      form.resetFields();
      onClose();
      if (typeof onCreated === 'function') {
        onCreated();
      }
    } catch (requestError) {
      if (requestError?.errorFields) {
        return;
      }
      console.error(requestError);
      const detail = requestError?.response?.data?.detail;
      setError(detail || 'Nao foi possivel criar o tenant.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Novo Tenant"
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Criar tenant"
      cancelText="Cancelar"
      confirmLoading={saving}
      destroyOnClose
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Erro ao criar tenant"
          description={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item
          name="display_name"
          label="Nome fantasia"
          rules={[{ required: true, message: 'Informe o nome fantasia.' }]}
        >
          <Input placeholder="Ex.: FisiCore Unidade Centro" />
        </Form.Item>

        <Form.Item
          name="slug"
          label="Slug"
          rules={[{ required: true, message: 'Informe o slug.' }]}
        >
          <Input placeholder="Ex.: fisicore-centro" />
        </Form.Item>

        <Form.Item name="legal_name" label="Razao social">
          <Input placeholder="Opcional" />
        </Form.Item>

        <Form.Item name="status" label="Status">
          <Select options={STATUS_OPTIONS} />
        </Form.Item>

        <Form.Item name="plan_code" label="Plano">
          <Select allowClear options={PLAN_OPTIONS} placeholder="Opcional" />
        </Form.Item>

        <Form.Item name="subscription_status" label="Status da assinatura">
          <Select allowClear options={SUBSCRIPTION_STATUS_OPTIONS} placeholder="Opcional" />
        </Form.Item>

        <Form.Item name="trial_ends_at" label="Trial ends">
          <DatePicker showTime style={{ width: '100%' }} placeholder="Opcional" />
        </Form.Item>

        <Form.Item name="public_signup_enabled" valuePropName="checked">
          <Checkbox>Permitir cadastro publico</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateTenantModal;
